'use client'

import React, { useState, useEffect } from "react";
import { Button, Col, Row, Card } from "react-bootstrap";
import { useData } from "@/storage/accessors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faCircleCheck, faCalendarDay, faBook } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router';
import useWheelId from "@/hooks/useWheelId";
import { useScreenSize } from '@/contexts/screenSizeContext';
import MeTabEditModal, { MeTabEditModalType } from "./MeTabEditModal";
import RoadMapWidget from "@/components/lifepart/RoadMapWidget";
import LevelUpModal from "@/components/lifepart/LevelUpModal";
import { useTaskModal } from "@/components/TaskModalContext";
import { TaskList } from "@/components/plan/TaskList";
import Icon from "@/components/Icon";
import { createMeSnapshot } from "@/components/me/meSnapshotUtils";
import useActualWheelId from "@/hooks/useActualWheelId";
import { useReadonly } from "@/contexts/readonlyContext";

const PRIMARY_COLOR = "var(--bs-primary)";
const PRIMARY_BG = "rgba(13, 110, 253, 0.08)";

export default function MeTab() {
    const wheelId = useActualWheelId();  // реальный UUID    
    const navWheelId = useWheelId();
    const navigate = useNavigate();
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";
    const { wheels, roles, meSnapshots } = useData();
    const wheel = wheels.objects[wheelId];
    const taskModalContext = useTaskModal();
    const setTaskModal = taskModalContext?.setTaskModal;

    const [editModal, setEditModal] = useState<MeTabEditModalType>(null);
    const [showLevelUpModal, setShowLevelUpModal] = useState(false);
    const [oldLevel, setOldLevel] = useState(0);
    const [newLevel, setNewLevel] = useState(0);
    const [today, setToday] = useState(new Date());

    const { readonly } = useReadonly();

    // Обновляем дату раз в минуту для корректного отображения
    // виджета задач на сегодня при длительной работе приложения
    useEffect(() => {
        const interval = setInterval(() => {
            setToday(new Date());
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    const currentLifePartUuid = wheel?.currentLifePart;
    const currentLifePart = currentLifePartUuid ? roles.objects[currentLifePartUuid] : undefined;

    const handleOpenTask = (taskUuid: string) => {
        if (setTaskModal) {
            setTaskModal({ taskUUID: taskUuid });
        }
    };

    const handleUpgrade = (step: number) => {
        if (readonly) return;
        if (currentLifePartUuid) {
            setOldLevel(currentLifePart?.startpoint || 0);
            setNewLevel(step);
            setShowLevelUpModal(true);
            roles.save(currentLifePartUuid, { startpoint: step });
        }
    };

    const handleSave = (data: {
        whoAmI?: string;
        myMission?: string;
        whoAmIRating?: number;
        myMissionRating?: number;
    }) => {
        if (readonly) return;
        wheels.save('wheel', data);

        // Создаём или обновляем снимок с новыми данными
        const todaySnapshots = Object.values(meSnapshots.objects)
            .filter(s => s.date === new Date().toISOString().split('T')[0]);

        const snapshotData = {
            whoAmI: data.whoAmI ?? wheel?.whoAmI ?? '',
            myMission: data.myMission ?? wheel?.myMission ?? '',
            whoAmIRating: data.whoAmIRating ?? wheel?.whoAmIRating ?? 0,
            myMissionRating: data.myMissionRating ?? wheel?.myMissionRating ?? 0,
        };

        if (todaySnapshots.length > 0) {
            // Обновляем существующий снимок
            const existingSnapshot = todaySnapshots[0];
            meSnapshots.save(existingSnapshot.uuid, snapshotData);
            console.log('Me snapshot updated:', existingSnapshot.uuid);
        } else {
            // Создаём новый снимок
            const snapshot = createMeSnapshot(
                wheelId,
                snapshotData.whoAmI,
                snapshotData.myMission,
                snapshotData.whoAmIRating,
                snapshotData.myMissionRating
            );
            meSnapshots.create(snapshot);
            console.log('Me snapshot saved:', snapshot.uuid, snapshot.date);
        }
    };

    const getGlowClass = (rating: number): string => {
        if (rating >= 10) return 'glow-rating-10';
        if (rating >= 9) return 'glow-rating-9';
        if (rating >= 8) return 'glow-rating-8';
        if (rating >= 7) return 'glow-rating-7';
        if (rating >= 6) return 'glow-rating-6';
        if (rating >= 5) return 'glow-rating-5';
        if (rating >= 4) return 'glow-rating-4';
        if (rating >= 3) return 'glow-rating-3';
        if (rating >= 2) return 'glow-rating-2';
        if (rating >= 1) return 'glow-rating-1';
        return 'glow-rating-0';
    };

    const getRatingLabel = (rating: number): string => {
        if (rating >= 10) return 'Идеально!';
        if (rating >= 8) return 'Очень близко';
        if (rating >= 6) return 'Хорошо';
        if (rating >= 4) return 'Средне';
        if (rating >= 2) return 'Требует доработки';
        return 'Не определено';
    };

    const renderBlock = (
        title: string,
        text: string | undefined,
        rating: number,
        type: MeTabEditModalType
    ) => {
        const hasText = !!text?.trim();
        const glowClass = hasText ? getGlowClass(rating) : 'glow-rating-0';
        const ratingLabel = getRatingLabel(rating);

        return (
            <div className={`me-block ${glowClass}`}>
                <div className="me-block-header">
                    <h2 className="me-block-title">{title}</h2>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-block-edit-btn"
                        disabled={readonly}
                        onClick={() => setEditModal(type)}
                    >
                        <FontAwesomeIcon icon={faPenToSquare} /> Редактировать
                    </Button>
                </div>
                <div className="me-block-content">
                    {hasText ? (
                        <div className="me-block-text">{text}</div>
                    ) : (
                        <div className="me-block-empty">
                            Нажмите «Редактировать», чтобы записать свое определение.
                        </div>
                    )}
                </div>
                {hasText && (
                    <div className="me-block-footer">
                        <div className="me-rating-bar">
                            <div className="me-rating-label">
                                <small>Сила:</small>
                                <small className="me-rating-value">{ratingLabel}</small>
                            </div>
                            <div className="me-rating-dots align-items-center">
                                {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                                    <span
                                        key={value}
                                        className={`me-rating-dot ${value <= rating ? 'active' : ''}`}
                                    />
                                ))}
                                {rating === 10 && (
                                    <span
                                        className="me-rating-checkmark"
                                        style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            backgroundColor: '#0d6efd',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginLeft: 4,
                                            animation: 'lp-checkmark-pulse 0.5s ease-in-out',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faCircleCheck} style={{ color: 'white', fontSize: '12px' }} />
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Row className="g-3 mb-4">
                <Col lg={6}>
                    {renderBlock(
                        'Кто я',
                        wheel?.whoAmI,
                        wheel?.whoAmIRating || 0,
                        'whoAmI'
                    )}
                </Col>
                <Col lg={6}>
                    {renderBlock(
                        'Моя миссия',
                        wheel?.myMission,
                        wheel?.myMissionRating || 0,
                        'myMission'
                    )}
                </Col>
            </Row>
            {isMobile && (
                <Card className="mt-4 mb-4 text-center">
                    <Card.Body>
                        <Button variant="outline-primary" onClick={() => navigate(`/${navWheelId}/reflection`)}>
                            <Icon icon={faBook} className="me-2" />
                            Рефлексия
                        </Button>
                    </Card.Body>
                </Card>
            )}
            {currentLifePart && (
                <Row className="g-3">
                    <Col lg={6}>
                        <RoadMapWidget
                            lifepartUuid={currentLifePart.uuid}
                            currentStartpoint={currentLifePart.startpoint || 0}
                            onUpgrade={handleUpgrade}
                            onOpenTask={handleOpenTask}
                            showNavigateButton={true}
                        />
                    </Col>
                    <Col lg={6}>
                        <div className="">
                            <Card className="border-2 shadow-sm" style={{ borderColor: PRIMARY_COLOR }}>
                                <Card.Header
                                    className="d-flex align-items-center gap-2"
                                    style={{ backgroundColor: PRIMARY_BG }}
                                >
                                    <Icon icon={faCalendarDay} style={{ color: PRIMARY_COLOR }} />
                                    <strong style={{ color: PRIMARY_COLOR }}>Задачи на сегодня</strong>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <TaskList date={today} />
                                </Card.Body>
                            </Card>
                        </div>
                    </Col>
                </Row>

            )}

            <MeTabEditModal
                show={!!editModal}
                onHide={() => setEditModal(null)}
                type={editModal}
                whoAmI={wheel?.whoAmI || ''}
                myMission={wheel?.myMission || ''}
                whoAmIRating={wheel?.whoAmIRating || 0}
                myMissionRating={wheel?.myMissionRating || 0}
                onSave={handleSave}
            />

            <LevelUpModal
                show={showLevelUpModal}
                onHide={() => setShowLevelUpModal(false)}
                lifePart={currentLifePart}
                oldLevel={oldLevel}
                newLevel={newLevel}
            />
        </>
    );
}